// Shared helpers: formatters, badges, color maps

export const COLOUR_MAP = {
  White: '#d8d8d8', Grey: '#888', Black: '#222', Silver: '#aaa',
  Blue: '#4a90d9', Green: '#4caf50', Orange: '#e65100', Red: '#c0392b',
};

export const LOCATION_LABELS = {
  south:    'South St',
  garling:  'Garling St',
  melville: 'Melville',
  offsite:  'Offsite',
  transit:  'Transit',
};

export const LOCATION_IDS = ['south', 'garling', 'melville', 'offsite', 'transit'];

export const LOCATION_SUBLABELS = {
  south:    'Stock Yard',
  garling:  'Workshop / Tint',
  melville: 'Dealership',
  offsite:  'Off-premises',
  transit:  'In transit',
};

// Deal type (shown as "Status" in the UI) — mirrors the spreadsheet dropdown.
// Each maps to a CSS class for its badge colour.
export const DEAL_STATUS_OPTIONS = ['SOLD', 'CUSTOMER', 'DEMO', 'STOCK', 'USED'];

export function dealClass(d) {
  switch (d) {
    case 'SOLD':     return 'deal-sold';
    case 'CUSTOMER': return 'deal-customer';
    case 'DEMO':     return 'deal-demo';
    case 'STOCK':    return 'deal-stock';
    case 'USED':     return 'deal-used';
    default:         return 'deal-stock';
  }
}

// Brands with stock-number rules and free-text-model flag (mirrors Acts app rules)
//   prefix : auto-prefix added to stock numbers (typed-in part is digits-only)
//   digits : expected length of digit portion (for placeholder hint)
//   freeModel : true means model is free text (no preset list)
export const BRANDS = [
  { id: 'KIA',     label: 'KIA',       prefix: 'K', digits: 6, freeModel: false, stockHint: 'K + 6 digits (e.g. K123456)' },
  { id: 'SUZUKI',  label: 'SUZUKI',    prefix: '',  digits: 6, freeModel: false, stockHint: '6 digits (e.g. 123456)' },
  { id: 'GWM',     label: 'GWM',       prefix: 'G', digits: 6, freeModel: false, stockHint: 'G + 6 digits (e.g. G123456)' },
  { id: 'GAC',     label: 'GAC',       prefix: 'A', digits: 6, freeModel: false, alphanumeric: true, stockHint: 'A + last 6 of VIN (letters ok, e.g. A1B2C3)' },
  { id: 'USED',    label: 'USED CARS', prefix: '',  digits: 5, freeModel: true,  stockHint: '5 digits (e.g. 12345)' },
  { id: 'SERVICE', label: 'SERVICE',   prefix: '',  digits: 0, freeModel: true,  stockHint: 'Number plate (e.g. 1ABC234)' },
];

export const BRAND_OPTIONS = BRANDS.map((b) => b.id);

// Models per brand (synced with Acts car_models table — Apr 2026)
export const MODELS_BY_BRAND = {
  KIA:    ['Picanto', 'Stonic', 'Sportage', 'Sorento', 'Carnival', 'K4', 'EV3', 'EV5', 'EV9', 'Tasman'],
  SUZUKI: ['Vitara', 'Swift', 'Jimny', 'Fronx', 'Ignis', 'Baleno', 'S-Cross'],
  GWM:    ['H6', 'H7', 'Tank 300', 'Tank 500', 'Cannon', 'Alpha', 'Jolion', 'Ora'],
  // USED + SERVICE: free-text input, no preset list
};

export const SERVICE_OPTIONS = [
  { id: 'Detail',         label: '✨ Detail' },
  { id: 'Window Tint',    label: '🪟 Window Tint' },
  { id: 'Paint Pack',     label: '🛡️ Paint Pack' },
  { id: 'Mats',           label: '🧼 Mats' },
  { id: 'Towbar',         label: '🔩 Towbar' },
  { id: 'Pre-Delivery',   label: '🔧 Pre-Delivery' },
  { id: 'Cargo Tray',     label: '📦 Cargo Tray' },
  { id: 'Dashcam',        label: '📷 Dashcam' },
];

export const ROLE_NAV = {
  Sales:       [{ l:'Dashboard',i:'📊',v:'dash' },{ l:'Add Vehicle',i:'➕',v:'add' },{ l:'Deliveries',i:'🚗',v:'del' },{ l:'Archive',i:'📦',v:'archive' }],
  // Department managers: full edit + approve their direct reports (Users page filtered)
  SalesMgr:    [{ l:'Dashboard',i:'📊',v:'dash' },{ l:'Add Vehicle',i:'➕',v:'add' },{ l:'Deliveries',i:'🚗',v:'del' },{ l:'Archive',i:'📦',v:'archive' },{ l:'Sales Team',i:'🧑‍💼',v:'users' }],
  WorkshopMgr: [{ l:'Dashboard',i:'📊',v:'dash' },{ l:'Add Vehicle',i:'➕',v:'add' },{ l:'Deliveries',i:'🚗',v:'del' },{ l:'Archive',i:'📦',v:'archive' },{ l:'Workshop Team',i:'🔧',v:'users' }],
  DetailMgr:   [{ l:'Dashboard',i:'📊',v:'dash' },{ l:'Deliveries',i:'🚗',v:'del' },{ l:'Archive',i:'📦',v:'archive' },{ l:'Detail Team',i:'✨',v:'users' }],
  TintMgr:     [{ l:'Dashboard',i:'📊',v:'dash' },{ l:'Deliveries',i:'🚗',v:'del' },{ l:'Archive',i:'📦',v:'archive' },{ l:'Tint Team',i:'🪟',v:'users' }],
  // Mechanic / Detail / Tint: NO Add Vehicle — restricted to their own column + checklist
  // My Queue is FIRST (becomes their default landing screen)
  Mechanic: [{ l:'My Queue',i:'🔧',v:'queue' },{ l:'Dashboard',i:'📊',v:'dash' },{ l:'Deliveries',i:'🚗',v:'del' },{ l:'Archive',i:'📦',v:'archive' }],
  Detail:   [{ l:'My Queue',i:'✨',v:'queue' },{ l:'Dashboard',i:'📊',v:'dash' },{ l:'Deliveries',i:'🚗',v:'del' },{ l:'Archive',i:'📦',v:'archive' }],
  Tint:     [{ l:'My Queue',i:'🪟',v:'queue' },{ l:'Dashboard',i:'📊',v:'dash' },{ l:'Deliveries',i:'🚗',v:'del' },{ l:'Archive',i:'📦',v:'archive' }],
  DeliveryMgr: [{ l:'Dashboard',i:'📊',v:'dash' },{ l:'Deliveries',i:'🚗',v:'del' },{ l:'Add Vehicle',i:'➕',v:'add' },{ l:'Archive',i:'📦',v:'archive' }],
  Manager:  [{ l:'Dashboard',i:'📊',v:'dash' },{ l:'Add Vehicle',i:'➕',v:'add' },{ l:'Deliveries',i:'🚗',v:'del' },{ l:'Archive',i:'📦',v:'archive' },{ l:'Users',i:'👥',v:'users' },{ l:'Car Models',i:'🚘',v:'models' }],
};

// Which subordinate role each manager-role oversees (used to filter Users page)
export const MGR_APPROVES = {
  SalesMgr:    'Sales',
  WorkshopMgr: 'Mechanic',
  DetailMgr:   'Detail',
  TintMgr:     'Tint',
};

// Display labels for roles (longer/friendlier than the id)
// Where the keys are — one field, replaces the old Workshop/Dealership pair on
// the dashboard row. Two places, two states each; the key icon carries the
// state (grey = pending, yellow = ready) so the chip only has to say where.
export const KEYS_OPTIONS = [
  { id: 'workshop_pending', short: 'Workshop', label: 'Workshop — Pending',      ready: false, phrase: 'keys still with the workshop' },
  { id: 'workshop_ready',   short: 'Workshop', label: 'Workshop — Good to go',   ready: true,  phrase: 'keys ready at the workshop' },
  { id: 'delivery_pending', short: 'Delivery', label: 'Delivery dept — Pending', ready: false, phrase: 'keys on their way to the delivery dept' },
  { id: 'delivery_ready',   short: 'Delivery', label: 'Delivery dept — Ready',   ready: true,  phrase: 'keys at the delivery dept' },
];
export const KEYS_BY_ID = Object.fromEntries(KEYS_OPTIONS.map((k) => [k.id, k]));
export const keysOption = (v) => KEYS_BY_ID[v] || KEYS_BY_ID.workshop_pending;
// End of the line: keys handed over at the delivery department.
export const KEYS_DONE = 'delivery_ready';

// Progress across the four stages — 'na' stages are excluded, keys don't count.
export const STAGE_COLS = ['status_workshop', 'status_tint', 'status_detail', 'status_paint'];
export const STAGE_LABELS = ['WORKSHOP', 'TINT', 'DETAIL', 'PAINT PACK'];
export function stageProgress(v) {
  const applicable = STAGE_COLS.map((c) => v[c]).filter((s) => s !== 'na');
  const done = applicable.filter((s) => s === 'done').length;
  return applicable.length ? Math.round((done / applicable.length) * 100) : 0;
}

// ── Delivery board ────────────────────────────────────────────────────────
// One source of truth for the card badge, the rail colour and the filter
// chips, so a chip count can never disagree with what the cards show.
// Blocked wins over ready: a flagged car is not going anywhere.
export function deliveryStatus(v) {
  if (v.blocker_note) return 'BLOCKED';
  const stages = STAGE_COLS.map((c) => v[c]).filter((s) => s !== 'na');
  if (!stages.length) return 'READY';
  if (stages.every((s) => s === 'done')) return 'READY';
  if (stages.some((s) => s === 'done' || s === 'prog')) return 'IN_PROGRESS';
  return 'NOT_STARTED';
}

// The note strip: one short sentence naming what the car is waiting on.
// No ETAs — nothing in the schema records how long a stage takes.
const STAGE_NAMES = { status_workshop: 'workshop', status_tint: 'tint', status_detail: 'detail', status_paint: 'paint pack' };
export function deliveryNote(v) {
  if (v.blocker_note) return { kind: 'block', text: `Blocked: ${v.blocker_note}` };

  const status = deliveryStatus(v);
  if (status === 'READY') {
    const keys = keysOption(v.keys_status);
    return keys.id === 'delivery_ready'
      ? { kind: 'ok', text: 'Ready for handover — keys at the delivery dept' }
      : { kind: 'ok', text: `All work complete — ${keys.phrase}` };
  }

  const active = STAGE_COLS.filter((c) => v[c] === 'prog').map((c) => STAGE_NAMES[c]);
  if (active.length) return { kind: 'work', text: `${cap(list(active))} in progress` };

  const waiting = STAGE_COLS.filter((c) => v[c] === 'ns').map((c) => STAGE_NAMES[c]);
  if (waiting.length) return { kind: 'wait', text: `Waiting on ${list(waiting)}` };

  return { kind: 'wait', text: 'Waiting to start' };
}
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const list = (a) => (a.length > 1 ? `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}` : a[0]);

// delivery_time is free text and arrives as both '14:00' and '2:00 PM', so
// sorting and slot grouping go through minutes-since-midnight.
export function timeToMinutes(t) {
  const m = String(t || '').trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!m) return 24 * 60 + 1;                    // missing/unparseable sorts last
  let h = parseInt(m[1], 10);
  if (m[3]) {
    const pm = /^[Pp]/.test(m[3]);
    if (h === 12) h = pm ? 12 : 0;
    else if (pm) h += 12;
  }
  return h * 60 + parseInt(m[2], 10);
}

// "in 1h 05m" / "in 25m" / "overdue" against the slot start, today.
export function countdownTo(minutes) {
  if (minutes > 24 * 60) return null;
  const now = new Date();
  const diff = minutes - (now.getHours() * 60 + now.getMinutes());
  if (diff < 0) return { text: 'overdue', urgent: true };
  if (diff < 60) return { text: `in ${diff}m`, urgent: diff <= 30 };
  const h = Math.floor(diff / 60), m = diff % 60;
  return { text: m ? `in ${h}h ${String(m).padStart(2, '0')}m` : `in ${h}h`, urgent: false };
}

export const ROLE_LABELS = {
  Sales:       'Sales',
  SalesMgr:    'Sales Manager',
  WorkshopMgr: 'Workshop Manager',
  Mechanic:    'Mechanic',
  Detail:      'Detail',
  DetailMgr:   'Detail Manager',
  Tint:        'Tint',
  TintMgr:     'Tint Manager',
  DeliveryMgr: 'Delivery Manager',
  Manager:     'Manager',
};

export function roleLabel(role) {
  return ROLE_LABELS[role] || role;
}

// Roles allowed to create new vehicles (Add Vehicle)
export const ROLES_CAN_ADD = new Set(['Sales', 'SalesMgr', 'WorkshopMgr', 'DeliveryMgr', 'Manager']);

// What a given role is allowed to edit on a vehicle:
//   - Manager / Sales / WorkshopMgr / Noah → everything (no per-field restriction)
//   - Mechanic → ONLY status_workshop
//   - Detail   → ONLY status_detail
//   - Tint     → ONLY status_tint
//
// canEditField(role, field) returns true if the field is editable for that role.
export function canEditField(role, field) {
  // Full access: Manager + all department managers + Sales + Noah
  if (['Manager', 'Sales', 'SalesMgr', 'WorkshopMgr', 'DetailMgr', 'TintMgr', 'DeliveryMgr'].includes(role)) return true;
  if (role === 'Mechanic') return field === 'status_workshop';
  if (role === 'Detail')   return field === 'status_detail';
  if (role === 'Tint')     return field === 'status_tint';
  return false;
}

export function canAddVehicle(role) {
  return ROLES_CAN_ADD.has(role);
}

// My Queue: which status_* column is "mine"
export const ROLE_STATUS_COL = {
  Mechanic:    'status_workshop',
  Detail:      'status_detail',
  Tint:        'status_tint',
  WorkshopMgr: 'status_workshop',
};

export const ROLE_CHECKLIST_TYPE = {
  Mechanic:    'workshop',
  Detail:      'detail',
  Tint:        'tint',
  DeliveryMgr: 'noah',  // internal checklist id stays as 'noah' (Final Delivery Checklist)
};

export function brandClass(b) {
  if (b === 'KIA')     return 'kia';
  if (b === 'SUZUKI')  return 'suz';
  if (b === 'GWM')     return 'gwm';
  if (b === 'GAC')     return 'gac';
  if (b === 'SERVICE') return 'svc';
  return 'used';
}

export function brandLabel(b) {
  const found = BRANDS.find((x) => x.id === b);
  return found ? found.label : b;
}

export function statusLabel(s) {
  return s === 'done' ? 'Complete' : s === 'prog' ? 'In progress' : s === 'ns' ? 'Not started' : 'N/A';
}

// One time format across the app: 12h with AM/PM. Data arrives both ways —
// the picker writes '2:00 PM', older/imported rows carry '14:00'.
export function formatTime(t) {
  const s = String(t || '').trim();
  const m = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!m) return s;
  const min = m[2];
  const ampm = m[3];
  let h = parseInt(m[1], 10);
  if (ampm) return `${h}:${min} ${ampm.toUpperCase()}`;
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;                    // 00:30 → 12:30 AM, 13:00 → 1:00 PM
  return `${h}:${min} ${suffix}`;
}

// Colours are typed by hand, so they arrive as WHITE / white / White.
export function titleCase(s) {
  return String(s || '').toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

export const JAMES_LABELS = ['— Pending', '🔑 Ready to Go', '🚐 Car Gone'];
export const NOAH_LABELS  = ['— Awaiting', '📥 Car Here', '✅ Good to Deliver'];

export function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function formatDate(iso) {
  if (!iso) return '';
  // iso may be 'YYYY-MM-DD' or full timestamp
  const s = String(iso).slice(0, 10);
  const [, m, d] = s.split('-');
  return `${d}/${m}`;
}

export function dayOfWeek(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dt.getDay()];
}

export function formatLongDate(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${days[dt.getDay()]}, ${d} ${months[m - 1]} ${y}`;
}
